import React, { useEffect, useState } from 'react';
import { categoryService } from '../../services/categoryService';
import { CourseCategory } from '../../types';

const AdminCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState({ name: '', description: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await categoryService.list(query || undefined);
      setCategories(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      alert('Tên danh mục không được trống');
      return;
    }
    setCreating(true);
    try {
      const category = await categoryService.create({
        name: newCategory.name.trim(),
        description: newCategory.description.trim() || undefined,
      });
      setCategories((prev) => [category, ...prev]);
      setNewCategory({ name: '', description: '' });
      alert('Tạo danh mục thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể tạo danh mục');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (category: CourseCategory) => {
    setEditingId(category.id);
    setEditingData({
      name: category.name,
      description: category.description || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingData({ name: '', description: '' });
  };

  const saveEdit = async (categoryId: number) => {
    if (!editingData.name.trim()) {
      alert('Tên danh mục không được trống');
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await categoryService.update(categoryId, {
        name: editingData.name.trim(),
        description: editingData.description.trim() || undefined,
      });
      setCategories((prev) => prev.map((cat) => (cat.id === categoryId ? updated : cat)));
      cancelEdit();
      alert('Cập nhật danh mục thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể cập nhật danh mục');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteCategory = async (categoryId: number) => {
    if (!window.confirm('Xóa danh mục này?')) return;
    try {
      await categoryService.remove(categoryId);
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể xóa danh mục');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCategories();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Thêm danh mục khóa học</h2>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreate}>
          <label className="text-sm">
            <span className="text-gray-700">Tên danh mục*</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCategory.name}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Mô tả</span>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              value={newCategory.description}
              onChange={(e) => setNewCategory((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Đang tạo...' : 'Tạo danh mục'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-lg font-medium">Danh sách danh mục</h2>
          <form className="flex gap-2" onSubmit={handleSearch}>
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Tìm theo tên..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="submit"
              className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm"
              disabled={loading}
            >
              {loading ? 'Đang tìm...' : 'Lọc'}
            </button>
          </form>
        </div>

        <div className="p-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Tên danh mục</th>
                <th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">Mô tả</th>
                <th className="py-2 pr-4">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const isEditing = editingId === cat.id;
                return (
                  <tr key={cat.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">{cat.id}</td>
                    <td className="py-2 pr-4">
                      {isEditing ? (
                        <input
                          className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                          value={editingData.name}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, name: e.target.value }))}
                        />
                      ) : (
                        cat.name
                      )}
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{cat.slug}</td>
                    <td className="py-2 pr-4">
                      {isEditing ? (
                        <textarea
                          className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                          rows={2}
                          value={editingData.description}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, description: e.target.value }))}
                        />
                      ) : (
                        cat.description || '-'
                      )}
                    </td>
                    <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveEdit(cat.id)}
                            className="px-3 py-1 rounded-lg bg-green-600 text-white"
                            disabled={savingEdit}
                          >
                            {savingEdit ? 'Đang lưu...' : 'Lưu'}
                          </button>
                          <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700">
                            Hủy
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(cat)}
                            className="px-3 py-1 rounded-lg bg-blue-600 text-white"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => deleteCategory(cat.id)}
                            className="px-3 py-1 rounded-lg bg-red-600 text-white"
                          >
                            Xóa
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCategoriesPage;



